// Port of ios/Sources/ViewModels/ChannelInfoPresentation.swift.
//
// The channel-info sheet's rules with a wrong answer (PARITY: "Leave / archive a channel"),
// and the permission-snapshot codec the reconcile writes into `sync_state`.
//
// Leaving applies to REAL channels: a direct message has no membership to resign (Mattermost
// closes those, it does not leave them).

export function canLeave(channelType: string): boolean {
  return channelType === 'O' || channelType === 'P';
}

/** A missing or invalid cache entry is unknown, never an empty channel. */
export function cachedMemberCount(value: string | null | undefined): number | null {
  if (value == null) return null;
  // Swift's `Int64(_:)` accepts an optional sign and digits only — no spaces, no decimals.
  if (!/^[+-]?\d+$/.test(value)) return null;
  const count = Number(value);
  if (!Number.isSafeInteger(count) || count < 0) return null;
  return count;
}

export function roleNames(values: readonly string[]): Set<string> {
  const result = new Set<string>();
  for (const value of values) {
    for (const token of value.split(/[ \t\n]/)) {
      const trimmed = token.trim();
      if (trimmed !== '') result.add(trimmed);
    }
  }
  return result;
}

export function permissionsFor(roleNamesSet: ReadonlySet<string>, permissionsByRole: ReadonlyMap<string, ReadonlySet<string>>): Set<string> {
  const result = new Set<string>();
  for (const role of roleNamesSet) {
    for (const permission of permissionsByRole.get(role) ?? []) result.add(permission);
  }
  return result;
}

/** Sorted, newline-joined — deterministic, so an unchanged snapshot writes the same bytes. */
export function encodePermissions(permissions: ReadonlySet<string>): string {
  return [...permissions].sort(swiftOrder).join('\n');
}

export function decodePermissions(value: string | null | undefined): Set<string> {
  if (value == null) return new Set();
  return new Set(
    value
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s !== ''),
  );
}

/** Town Square is the server's non-archivable default channel. */
export function canArchive(channelType: string, channelName: string, permissions: ReadonlySet<string>): boolean {
  if (channelName === 'town-square') return false;
  if (permissions.has('manage_system')) return channelType === 'O' || channelType === 'P';
  switch (channelType) {
    case 'O':
      return permissions.has('delete_public_channel');
    case 'P':
      return permissions.has('delete_private_channel');
    default:
      return false;
  }
}

/** Swift's `sorted()` on ASCII permission names — plain code-unit order. */
function swiftOrder(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Namespace form, for `ChannelInfoPresentation.canArchive(...)` call sites. */
export const ChannelInfoPresentation = {
  canLeave,
  cachedMemberCount,
  roleNames,
  permissionsFor,
  encodePermissions,
  decodePermissions,
  canArchive,
} as const;
